import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAssistant, useAssistantsByIds, useAssistantsSimple } from '@/hooks/api/useAssistants'
import {
  conversationKeys,
  useConversation,
  useInfiniteConversations,
} from '@/hooks/api/useConversations'
import { useBreakpoint } from '@/hooks/use-breakpoint'
import { useAutoRefresh } from '@/hooks/useAutoRefresh'
import { cn } from '@/lib/utils'
import { useFilterStore } from '@/stores/filterStore'
import { InboxDetails, type InboxDetailsTab } from './InboxDetails'
import { InboxList } from './InboxList'
import { InboxThread } from './InboxThread'
import {
  buildInboxFilter,
  EMPTY_INBOX_FILTERS,
  parseInboxFilters,
  serializeInboxFilters,
  type InboxFilters,
} from './inboxFilters'
import { isTypingTarget } from './keyboard'
import {
  needsMorePagesForSelection,
  nextSelectionChase,
  type SelectionChase,
} from './selectionPaging'
import { useSettledInboxFilters } from './useSettledInboxFilters'
import { getViewedConversationIds, markConversationAsViewed } from './viewedConversations'
import type { Conversation } from '@/types/conversation'

const PERSIST_KEY = 'conversations-inbox'

const DETAILS_TABS: InboxDetailsTab[] = ['phone', 'whatsapp', 'admin']

// Notes sit on the overview. Older /notes and /summary links open that view.
const toDetailsTab = (path?: string): InboxDetailsTab =>
  DETAILS_TABS.find((tab) => tab === path) ?? 'overview'
const tabPath = (tab: InboxDetailsTab) => tab

export function UnifiedInbox() {
  const { id: selectedId, tab } = useParams<{ id: string; tab: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const isDesktop = useBreakpoint('lg')
  const isWide = useBreakpoint('xl')
  const { getFilters, setFilters, clearFilters } = useFilterStore()

  const filterParam = searchParams.get('filter')
  // A shared link wins. With no `filter` param, the last filters saved in this browser apply
  // until the effect below copies them into the URL.
  const filters = useMemo((): InboxFilters => {
    if (searchParams.has('filter')) return parseInboxFilters(filterParam)
    const persisted = getFilters(PERSIST_KEY)
    return persisted ? parseInboxFilters(JSON.stringify(persisted)) : EMPTY_INBOX_FILTERS
  }, [searchParams, filterParam, getFilters])
  const filterQuery = serializeInboxFilters(filters)

  // null follows the screen width: details are open by default only where they fit
  const [detailsOpen, setDetailsOpen] = useState<boolean | null>(null)
  const isDetailsOpen = detailsOpen ?? isWide
  const [viewedIds, setViewedIds] = useState(getViewedConversationIds)

  const handleFiltersChange = useCallback(
    (next: InboxFilters) => {
      const serialized = serializeInboxFilters(next)
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current)
          if (serialized) params.set('filter', serialized)
          else params.delete('filter')
          return params
        },
        { replace: true }
      )
      if (serialized) setFilters(PERSIST_KEY, JSON.parse(serialized) as Record<string, unknown>)
      else clearFilters(PERSIST_KEY)
    },
    [setSearchParams, setFilters, clearFilters]
  )

  useEffect(() => {
    if ((searchParams.get('filter') ?? null) === filterQuery) return
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        if (filterQuery) params.set('filter', filterQuery)
        else params.delete('filter')
        return params
      },
      { replace: true }
    )
  }, [filterQuery, searchParams, setSearchParams])

  useEffect(() => {
    if (!filterQuery) return
    setFilters(PERSIST_KEY, JSON.parse(filterQuery) as Record<string, unknown>)
  }, [filterQuery, setFilters])

  const pathWithFilters = useCallback(
    (path: string) => {
      const params = new URLSearchParams(searchParams)
      if (filterQuery) params.set('filter', filterQuery)
      else params.delete('filter')
      const query = params.toString()
      return query ? `${path}?${query}` : path
    },
    [searchParams, filterQuery]
  )

  useEffect(() => {
    if (!selectedId || (tab !== 'notes' && tab !== 'summary')) return
    navigate(pathWithFilters(`/conversations/${selectedId}/overview`), { replace: true })
  }, [selectedId, tab, navigate, pathWithFilters])

  useAutoRefresh({ queryKey: [...conversationKeys.lists()], refreshOnFocus: true })

  // The full list feeds the booking-system filter. It is capped, so names on
  // conversation rows are resolved from the assistants on the loaded conversations.
  const { data: assistants = [] } = useAssistantsSimple()
  // Tags settle before they hit the list. The chips still update immediately.
  const queryFilters = useSettledInboxFilters(filters)
  const raFilter = useMemo(
    () => buildInboxFilter(queryFilters, assistants),
    [queryFilters, assistants]
  )
  const list = useInfiniteConversations(raFilter)
  const { data: conversation, isLoading: isConversationLoading } = useConversation(selectedId)

  // Activity moves conversations between pages while scrolling, so drop repeats
  const conversations = useMemo(() => {
    const seen = new Set<string>()
    return (list.data?.pages.flatMap((page) => page.data) ?? []).filter((item) => {
      const key = String(item.id)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [list.data])

  const assistantIds = useMemo(() => {
    const ids = new Set<number>()
    for (const item of conversations) {
      if (item.assistant_id != null) ids.add(item.assistant_id)
    }
    return [...ids].sort((a, b) => a - b)
  }, [conversations])
  const { data: assistantsForRows = [] } = useAssistantsByIds(assistantIds)
  const { data: assistantFull } = useAssistant(conversation?.assistant_id, {
    enabled: Boolean(conversation?.assistant_id),
  })

  const listAssistants = useMemo(() => {
    if (!assistantsForRows.length) return assistants
    const seen = new Set(assistants.map((item) => item.id))
    const extra = assistantsForRows.filter((item) => !seen.has(item.id))
    return extra.length ? [...assistants, ...extra] : assistants
  }, [assistants, assistantsForRows])

  const assistant =
    assistantFull ?? listAssistants.find((item) => item.id === conversation?.assistant_id)

  const detailsTab = toDetailsTab(tab)

  const openConversation = useCallback(
    (item: Conversation, options?: { replace?: boolean }) => {
      markConversationAsViewed(item.id)
      setViewedIds((current) => new Set(current).add(String(item.id)))
      // Mobile opens on the transcript; WhatsApp-only conversations fall back to their WhatsApp tab
      navigate(
        pathWithFilters(`/conversations/${item.id}/${isDesktop ? tabPath(detailsTab) : 'phone'}`),
        options
      )
    },
    [navigate, detailsTab, isDesktop, pathWithFilters]
  )

  const changeDetailsTab = useCallback(
    (next: InboxDetailsTab) =>
      navigate(pathWithFilters(`/conversations/${selectedId}/${tabPath(next)}`), { replace: true }),
    [navigate, selectedId, pathWithFilters]
  )

  useEffect(() => {
    if (!selectedId && isDesktop && conversations[0]) {
      navigate(pathWithFilters(`/conversations/${conversations[0].id}/overview`), { replace: true })
    }
  }, [selectedId, isDesktop, conversations, navigate, pathWithFilters])

  const { hasNextPage, isFetchingNextPage, fetchNextPage, isPlaceholderData } = list
  const listFilterKey = filterQuery ?? ''
  const selectionChase = useRef<SelectionChase>({ selectedId, filterKey: listFilterKey })
  const chased = nextSelectionChase(selectionChase.current, selectedId, listFilterKey)
  selectionChase.current = chased.chase

  useEffect(() => {
    // Placeholder rows belong to the previous filter, so paging them would request the wrong pages.
    if (!chased.follow || isPlaceholderData) return
    if (!conversation || String(conversation.id) !== selectedId) return
    if (!hasNextPage || isFetchingNextPage) return
    if (needsMorePagesForSelection(conversations, conversation)) fetchNextPage()
  }, [
    chased.follow,
    isPlaceholderData,
    conversation,
    selectedId,
    conversations,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ])

  useEffect(() => {
    if (!selectedId) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      )
        return
      if (isTypingTarget(event.target)) return
      // Tabs and popovers use the arrow keys themselves
      if (
        event.target instanceof Element &&
        event.target.closest('[role="tablist"], [role="dialog"]')
      )
        return

      const current = conversations.findIndex((item) => String(item.id) === selectedId)
      if (current === -1) return
      const next = current + (event.key === 'ArrowLeft' ? -1 : 1)

      if (next >= conversations.length) {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage()
        return
      }
      if (next >= 0) openConversation(conversations[next], { replace: true })
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedId, conversations, openConversation, hasNextPage, isFetchingNextPage, fetchNextPage])

  const showList = isDesktop || !selectedId
  const showThread = isDesktop || !!selectedId

  return (
    <div
      className={cn(
        'bg-gray-25 relative -m-4 grid grid-rows-[minmax(0,1fr)] lg:-m-6 dark:bg-gray-950',
        'h-[calc(100dvh-4rem-var(--app-banner-height,0px))] lg:h-[calc(100dvh-var(--app-banner-height,0px))]',
        'grid-cols-1 lg:grid-cols-[323px_minmax(0,1fr)]',
        isWide && isDetailsOpen && conversation && 'xl:grid-cols-[323px_minmax(0,1fr)_344px]'
      )}
    >
      {showList && (
        <InboxList
          conversations={conversations}
          assistants={listAssistants}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={() => handleFiltersChange(EMPTY_INBOX_FILTERS)}
          selectedId={selectedId}
          viewedIds={viewedIds}
          onSelect={openConversation}
          isLoading={list.isLoading}
          isUpdating={isPlaceholderData}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={fetchNextPage}
        />
      )}

      {showThread && (
        <InboxThread
          conversation={conversation}
          isLoading={!!selectedId && isConversationLoading}
          isDetailsOpen={isDetailsOpen}
          detailsOverlay={isDesktop && isDetailsOpen && !isWide}
          onToggleDetails={() => setDetailsOpen(!isDetailsOpen)}
          onBack={isDesktop ? undefined : () => navigate(pathWithFilters('/conversations'))}
          tabs={
            isDesktop ? undefined : { assistant, tab: detailsTab, onTabChange: changeDetailsTab }
          }
        />
      )}

      {isDesktop && conversation && isDetailsOpen && (
        <InboxDetails
          key={conversation.id}
          conversation={conversation}
          assistant={assistant}
          tab={detailsTab}
          onTabChange={changeDetailsTab}
          className={
            isWide
              ? undefined
              : 'bg-gray-25 absolute inset-y-0 right-0 z-20 w-86 max-w-full shadow-xl dark:bg-gray-950'
          }
        />
      )}
    </div>
  )
}
